import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LogOut, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { stopImpersonationFlow, isImpersonating } from '@/lib/auth/impersonation';
import { toast } from 'sonner';

export default function ImpersonationBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [stopping, setStopping] = useState(false);

  const handleStopImpersonating = async () => {
    try {
      setStopping(true);
      await stopImpersonationFlow(navigate);
    } catch (error) {
      console.error('[ImpersonationBanner] Error stopping impersonation:', error);
      toast.error(t('superAdmin.impersonation.stopError'));
    } finally {
      // Le banner ne se démonte pas (return null) — reset obligatoire pour la prochaine impersonation.
      setStopping(false);
    }
  };

  if (!isImpersonating()) {
    return null;
  }

  const displayName = user?.email || user?.full_name || user?.first_name || 'this user';

  return (
    <div className="relative z-0 w-full shrink-0 border-b border-gray-200 bg-gray-100 text-gray-700">
      <div className="flex min-h-8 w-full flex-col items-start justify-between gap-1.5 px-4 py-1.5 text-xs sm:flex-row sm:items-center sm:gap-0 sm:py-0 nav:pl-[calc(var(--sidebar-width)+1.25rem)] nav:pr-5">
        <div className="flex min-w-0 items-center">
          <div className="mr-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
          <span className="truncate">
            {t('superAdmin.impersonation.banner', {
              name: displayName,
            })}
          </span>
        </div>
        <button
          type="button"
          onClick={handleStopImpersonating}
          disabled={stopping}
          className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded px-2 py-1 text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {stopping ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <LogOut className="h-3.5 w-3.5" />
          )}
          <span>{t('superAdmin.impersonation.stop')}</span>
        </button>
      </div>
    </div>
  );
}
